import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { formatCustomerNameForDisplay } from '../../utils/payment-success-modal.util';
import type { FamilySalePlateSummary } from '../../types/family-sale.types';

export interface SuccessModalData {
  title: string;
  description: string;
  buttonText?: string;
  showTimeInfo?: boolean;
  timeInfo?: string;
  packageName?: string;
  customerName?: string;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  /** When true (and a consumer binds `openGate`), render the "Open Gate" button. */
  showOpenGate?: boolean;
}

/**
 * Success modal component that displays a standardized confirmation dialog with optional order and vehicle details.
 * @component
 * @selector app-success-modal
 * @example
 *   <app-success-modal [isVisible]="true" [data]="modalData" (close)="handleClose()" (action)="handleAction()"></app-success-modal>
 * @input boolean isVisible - Controls the visibility of the modal. Default: false.
 * @input SuccessModalData data - Configuration object for the modal content. Typical shape: { title, description, buttonText, showTimeInfo, timeInfo, packageName?, customerName?, vehicleYear?, vehicleMake?, vehicleModel? }. Default: { title: 'Success!', description: 'Operation completed successfully.', buttonText: 'Okay', showTimeInfo: false, timeInfo: '' }.
 * @output EventEmitter<void> close - Emitted when the modal should be dismissed (triggered by close button, overlay click, or after default action).
 * @output EventEmitter<void> action - Emitted when the primary action button is pressed. Consumers can perform custom behavior; the component also emits close by default after action.
 * @output EventEmitter<void> printInvoice - Emitted when Print Receipt is pressed. The button only renders when a consumer subscribes to this output (printInvoice.observed).
 * @output EventEmitter<void> openGate - Emitted when Open Gate is pressed. The button only renders when a consumer subscribes to this output (openGate.observed) AND data.showOpenGate is true (order lane has a manual gate).
 * @input boolean isOpeningGate - Reflects an in-flight gate-open request so the Open Gate button can show a disabled/loading state.
 *
 * Purpose:
 * - Provides a reusable success/confirmation modal that can display a title, description, an action button, optional time info, and structured order/vehicle details when available.
 *
 * Key methods:
 * - onClose(): Emits the close event to request modal dismissal.
 * - onAction(): Emits the action event for external handling and then emits close to close the modal by default.
 * - onOverlayClick(event: Event): Detects clicks on the backdrop/overlay and closes the modal when the overlay itself (not an inner element) is clicked.
 *
 * Computed properties:
 * - hasOrderDetails: boolean — Returns true when packageName, customerName and vehicle information are present, allowing a structured order layout to be shown.
 * - vehicleDisplay: string — Constructs a user-friendly vehicle string from vehicleYear, vehicleMake and vehicleModel (e.g., "2022 Toyota Camry").
 *
 * Lifecycle:
 * - This component does not implement explicit Angular lifecycle hooks (e.g., OnInit). It is a presentational/interaction component driven by inputs and emitted events.
 */
@Component({
  selector: 'app-success-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './success-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./success-modal.component.scss'],
})
export class SuccessModalComponent {
  @Input() isVisible = false;
  @Input() data: SuccessModalData = {
    title: 'Success!',
    description: 'Operation completed successfully.',
    buttonText: 'Okay',
    showTimeInfo: false,
    timeInfo: '',
  };
  /** Reflects an in-flight gate-open request (disables the Open Gate button). */
  @Input() isOpeningGate = false;
  /** Family Plan sale (CWS-1580): one row per plate. Absent for every other sale. */
  @Input() plates: FamilySalePlateSummary[] | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() action = new EventEmitter<void>();
  /** Alias for `action` (legacy templates). Both emit on primary button click. */
  @Output() primaryAction = new EventEmitter<void>();
  @Output() printInvoice = new EventEmitter<void>();
  @Output() openGate = new EventEmitter<void>();

  onClose(): void {
    this.close.emit();
  }

  onAction(): void {
    this.action.emit();
    this.primaryAction.emit();
    this.close.emit();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  get hasPlates(): boolean {
    return !!this.plates?.length;
  }

  get hasRefundedPlates(): boolean {
    return !!this.plates?.some(plate => plate.status === 'refunded');
  }

  get displayCustomerName(): string {
    return formatCustomerNameForDisplay(this.data.customerName);
  }

  get hasOrderDetails(): boolean {
    return Boolean(
      (this.data.packageName && !this.isEmptyOrUnknown(this.data.packageName)) ||
      (this.data.customerName && !this.isEmptyOrUnknown(this.data.customerName)) ||
      this.vehicleDisplay
    );
  }

  get vehicleDisplay(): string {
    const parts: string[] = [];
    const year = this.data.vehicleYear?.trim();
    if (year && year !== '0' && !this.isEmptyOrUnknown(year)) {
      parts.push(year);
    }
    if (this.data.vehicleMake && !this.isEmptyOrUnknown(this.data.vehicleMake)) {
      parts.push(this.data.vehicleMake.trim());
    }
    if (this.data.vehicleModel && !this.isEmptyOrUnknown(this.data.vehicleModel)) {
      parts.push(this.data.vehicleModel.trim());
    }
    return parts.join(' ') || '';
  }

  private isEmptyOrUnknown(value: string | undefined): boolean {
    return formatCustomerNameForDisplay(value) === 'N/A';
  }
}
